/**
 * Application Entity - Core aggregate root for admissions
 * 
 * An Application represents a family's submission for a specific academic year.
 * It contains one or more ChildApplication entries (nested entities).
 */
import { z } from 'zod';
import { 
  ApplicationId, 
  ApplicationCode, 
  ApplicantId, 
  AcademicYear,
  UserId,
  ChildApplicationId,
} from '../types/branded';
import { 
  ApplicationStatus, 
  Priority, 
  SchoolDivision, 
  GradeLevel,
} from '../types/enums';
import { 
  ChildInfoSchema, 
  HealthInfoSchema,
  MedicalRecordSchema,
  EnrollmentProgressSchema,
  EnrollmentStepDataSchema,
  AcceptanceLetterStepDataSchema,
  TuitionContractStepDataSchema,
  TransportationB6tStepDataSchema,
  RegistrationFeeStepDataSchema,
} from '../value-objects/index';
import { CreateApplicantInputSchema } from './applicant';
import { ApplicantSchema } from './applicant';
import { ApplicationDocumentSchema } from './document';
import { ApplicationNoteSchema } from './note';

// ============================================================================
// Child Application (Nested Entity)
// ============================================================================

export const ChildApplicationSchema = z.object({
  id: ChildApplicationId,
  applicationId: ApplicationId,
  childInfo: ChildInfoSchema,
  gradeApplyingFor: GradeLevel,
  schoolDivision: SchoolDivision,
  previousSchool: z.string().nullable().optional(),
  healthInfo: HealthInfoSchema.nullable().optional(),
  
  // v1.2: Enhanced medical and enrollment tracking
  medical: MedicalRecordSchema.nullable().optional(),
  enrollmentProgress: EnrollmentProgressSchema.nullable().optional(),
  enrollmentStepData: EnrollmentStepDataSchema.nullable().optional(),

  // v1.2: Per-student status/assignment for dashboard views
  status: ApplicationStatus.optional(),
  assignedTo: UserId.nullable().optional(),
  assigneeName: z.string().min(1).max(255).nullable().optional(),
  enrollmentDeadline: z.number().int().nullable().optional(),
});
export type ChildApplication = z.infer<typeof ChildApplicationSchema>;

// ============================================================================
// Application Entity (Aggregate Root)
// ============================================================================

export const ApplicationSchema = z.object({
  // Identity
  id: ApplicationId,
  code: ApplicationCode,
  applicantId: ApplicantId,
  
  // Core fields
  academicYear: AcademicYear,
  status: ApplicationStatus,
  
  // Dashboard features (per user decisions D1, D2)
  assignedTo: UserId.nullable().optional(),
  interviewDate: z.number().int().nullable().optional(), // Unix seconds
  interviewRequestStatus: z.enum(['pending', 'accepted', 'rejected', 'expired']).nullable().optional(),
  interviewRequestedAt: z.number().int().nullable().optional(), // Unix seconds
  requestedInterviewSlots: z.array(z.number().int()).nullable().optional(), // Unix seconds
  interviewRequestNotes: z.string().nullable().optional(),
  visitDate: z.number().int().nullable().optional(), // Unix seconds
  priority: Priority.default('normal'),
  
  // Nested entities
  children: z.array(ChildApplicationSchema).default([]),
  applicant: ApplicantSchema.nullable().optional(),
  documents: z.array(ApplicationDocumentSchema).default([]).optional(),
  notes: z.array(ApplicationNoteSchema).default([]).optional(),
  tuitionConfiguration: z.record(z.string(), z.unknown()).nullable().optional(),
  tuitionConfigurationDocumentId: z.string().nullable().optional(),
  financialAidEnabled: z.boolean().optional(),
  financialAidAccessDecision: z.enum(['enabled', 'revoked']).optional(),
  financialAidAccessUpdatedAt: z.number().int().nullable().optional(),
  financialAidAccessUpdatedBy: z.string().nullable().optional(),
  financialAidAccessUpdatedByRole: z.string().nullable().optional(),
  
  // v1.2: Enrollment deadline for accepted students
  enrollmentDeadline: z.number().int().nullable().optional(), // Unix seconds
  
  // Timestamps (Unix seconds, per GRPO-Amplify-Critical.md)
  submittedAt: z.number().int().nullable().optional(),
  acceptedAt: z.number().int().nullable().optional(), // v1.2
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type Application = z.infer<typeof ApplicationSchema>;

// ============================================================================
// Input Types (for mutations)
// ============================================================================

export const CreateApplicationInputSchema = z.object({
  applicantId: ApplicantId,
  academicYear: AcademicYear,
  children: z.array(z.object({
    childInfo: ChildInfoSchema,
    gradeApplyingFor: GradeLevel,
    schoolDivision: SchoolDivision,
    previousSchool: z.string().nullable().optional(),
    healthInfo: HealthInfoSchema.nullable().optional(),
    medical: MedicalRecordSchema.nullable().optional(),
    enrollmentProgress: EnrollmentProgressSchema.nullable().optional(),
    enrollmentStepData: EnrollmentStepDataSchema.nullable().optional(),
    status: ApplicationStatus.optional(),
    assignedTo: UserId.nullable().optional(),
    enrollmentDeadline: z.number().int().nullable().optional(),
  })).min(1),
});
export type CreateApplicationInput = z.infer<typeof CreateApplicationInputSchema>;

export const SubmitApplicationInputSchema = z.object({
  applicant: CreateApplicantInputSchema,
  academicYear: AcademicYear,
  interviewDate: z.number().int().nullable().optional(),
  visitDate: z.number().int().nullable().optional(),
  children: z.array(z.object({
    childInfo: ChildInfoSchema,
    gradeApplyingFor: GradeLevel,
    schoolDivision: SchoolDivision,
    previousSchool: z.string().nullable().optional(),
    healthInfo: HealthInfoSchema.nullable().optional(),
    medical: MedicalRecordSchema.nullable().optional(),
    enrollmentProgress: EnrollmentProgressSchema.nullable().optional(),
    enrollmentStepData: EnrollmentStepDataSchema.nullable().optional(),
    status: ApplicationStatus.optional(),
    assignedTo: UserId.nullable().optional(),
    enrollmentDeadline: z.number().int().nullable().optional(),
  })).min(1),
});
export type SubmitApplicationInput = z.infer<typeof SubmitApplicationInputSchema>;

export const UpdateApplicationStatusInputSchema = z.object({
  applicationId: ApplicationId,
  status: ApplicationStatus,
});
export type UpdateApplicationStatusInput = z.infer<typeof UpdateApplicationStatusInputSchema>;

export const AssignApplicationInputSchema = z.object({
  applicationId: ApplicationId,
  staffId: UserId,
});
export type AssignApplicationInput = z.infer<typeof AssignApplicationInputSchema>;

export const ScheduleInterviewInputSchema = z.object({
  applicationId: ApplicationId,
  interviewDate: z.number().int(), // Unix seconds
  staffId: UserId.optional(),
});
export type ScheduleInterviewInput = z.infer<typeof ScheduleInterviewInputSchema>;

export const RequestInterviewScheduleInputSchema = z.object({
  applicationId: ApplicationId,
  preferredSlots: z.array(z.number().int()).min(1),
  notes: z.string().max(2000).nullable().optional(),
});
export type RequestInterviewScheduleInput = z.infer<typeof RequestInterviewScheduleInputSchema>;

export const ScheduleVisitInputSchema = z.object({
  applicationId: ApplicationId,
  visitDate: z.number().int(), // Unix seconds
  staffId: UserId.optional(),
});
export type ScheduleVisitInput = z.infer<typeof ScheduleVisitInputSchema>;

export const SendApplicationMessageInputSchema = z.object({
  applicationId: ApplicationId,
  subject: z.string().min(1).max(255),
  message: z.string().min(1).max(10000),
});
export type SendApplicationMessageInput = z.infer<typeof SendApplicationMessageInputSchema>;

export const UpdateChildApplicationInputSchema = z.object({
  childApplicationId: ChildApplicationId,
  status: ApplicationStatus.optional(),
  assignedTo: UserId.nullable().optional(),
  assigneeName: z.string().min(1).max(255).optional(),
  enrollmentDeadline: z.number().int().nullable().optional(),
});
export type UpdateChildApplicationInput = z.infer<typeof UpdateChildApplicationInputSchema>;

// ============================================================================
// Filter Types (for queries)
// ============================================================================

export const ApplicationFilterSchema = z.object({
  status: z.array(ApplicationStatus).optional(),
  schoolDivision: z.array(SchoolDivision).optional(),
  gradeLevel: z.array(GradeLevel).optional(),
  assignedTo: UserId.nullable().optional(),
  academicYear: AcademicYear.optional(),
  priority: Priority.optional(),
  dateRange: z.object({
    field: z.enum(['submittedAt', 'interviewDate', 'visitDate', 'createdAt', 'acceptedAt', 'enrollmentDeadline']),
    start: z.number().int().optional(),
    end: z.number().int().optional(),
  }).optional(),
  search: z.string().optional(), // Family name search
  
  // v1.2: Enrollment-specific filters
  pendingEnrollment: z.boolean().optional(), // Filter for accepted_pending_enrollment
  enrollmentOverdue: z.boolean().optional(), // Past deadline, not enrolled
});
export type ApplicationFilter = z.infer<typeof ApplicationFilterSchema>;

// ============================================================================
// v1.2 Enrollment Input Types
// ============================================================================

export const AcceptApplicationInputSchema = z.object({
  applicationId: ApplicationId,
  enrollmentDeadlineDays: z.number().int().default(14), // Days from now
});
export type AcceptApplicationInput = z.infer<typeof AcceptApplicationInputSchema>;

export const UpdateEnrollmentProgressInputSchema = z.object({
  childApplicationId: ChildApplicationId,
  step: z.enum([
    'step1AcceptanceLetter',
    'step2RegistrationForm',
    'step3B6TForm',
    'step4TuitionContract',
    'step5Payment',
    'step6ImmunizationRecords',
  ]),
  completed: z.boolean(),
});
export type UpdateEnrollmentProgressInput = z.infer<typeof UpdateEnrollmentProgressInputSchema>;

export const UpdateMedicalRecordInputSchema = z.object({
  childApplicationId: ChildApplicationId,
  medical: MedicalRecordSchema,
});
export type UpdateMedicalRecordInput = z.infer<typeof UpdateMedicalRecordInputSchema>;

export const CompleteEnrollmentInputSchema = z.object({
  applicationId: ApplicationId,
});
export type CompleteEnrollmentInput = z.infer<typeof CompleteEnrollmentInputSchema>;

export const UpsertEnrollmentStepDataInputSchema = z.discriminatedUnion('step', [
  z.object({
    childApplicationId: ChildApplicationId,
    step: z.literal('step1AcceptanceLetter'),
    data: AcceptanceLetterStepDataSchema,
  }),
  z.object({
    childApplicationId: ChildApplicationId,
    step: z.literal('step4TuitionContract'),
    data: TuitionContractStepDataSchema,
  }),
  z.object({
    childApplicationId: ChildApplicationId,
    step: z.literal('step3B6TForm'),
    data: TransportationB6tStepDataSchema,
  }),
  z.object({
    childApplicationId: ChildApplicationId,
    step: z.literal('step5Payment'),
    data: RegistrationFeeStepDataSchema,
  }),
]);
export type UpsertEnrollmentStepDataInput = z.infer<typeof UpsertEnrollmentStepDataInputSchema>;
